import type { InputLabelProps, InputProps } from "@mui/material";
import { TextField } from "@mui/material";
import type { ReactNode } from "react";
import { useMemo } from "react";
import type { FieldValues, ValidationRule } from "react-hook-form";
import { Controller } from "react-hook-form";
import type { FormComponentProps } from "./FormComponentProps";

export const emailValidationPattern = {
  value: /^\S+@\S+\.\S+$/,
  message: "Wartość musi być poprawnym adresem email",
};

export const phoneNumberValidationPattern = {
  value: /^\+?\d{4,15}$/,
  message: "Wartość musi być poprawnym numerem telefonu",
};

export const passwordValidationPattern = {
  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,64}$/,
  message: "Hasło musi zawierać od 8 do 64 znaków, małą literę, dużą literę, cyfrę i znak specjalny",
};

interface FormTextFieldProps<TFieldValues extends FieldValues = FieldValues> extends FormComponentProps<TFieldValues> {
  readonly type?: "date" | "email" | "phone" | "password" | "text" | "url" | "time";
  readonly size?: "small" | "medium";
  readonly fullWidth?: boolean;
  readonly helperText?: ReactNode;
  readonly multiline?: boolean;
  readonly minRows?: number;
  readonly InputProps?: Partial<InputProps>;
  readonly InputLabelProps?: Partial<InputLabelProps>;
  readonly autoFocus?: boolean;
  readonly shrinkInputLabel?: boolean;
  readonly valueOverride?: string;
  readonly margin?: "normal" | "dense" | "none";
  readonly required?: boolean;
  readonly id?: string;
  readonly autoComplete?: string;
  readonly variant?: "outlined" | "filled" | "standard";
  readonly rules?: {
    required?: ValidationRule<boolean>;
    pattern?: ValidationRule<RegExp>;
    validate?: (value: string, formValues: TFieldValues) => string | undefined;
  };
}

function FormTextField<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  type,
  size,
  fullWidth = true,
  helperText,
  multiline,
  minRows,
  InputProps,
  InputLabelProps,
  autoFocus,
  disabled,
  shrinkInputLabel,
  valueOverride,
  margin,
  required,
  id,
  autoComplete,
  variant,
  rules,
}: FormTextFieldProps<TFieldValues>) {
  const slotProps = useMemo(
    () => ({
      input: InputProps,
      inputLabel: {
        ...(InputLabelProps ?? {}),
        shrink: shrinkInputLabel,
      },
    }),
    [InputProps, InputLabelProps, shrinkInputLabel],
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return (
          <TextField
            helperText={error ? error.message : (helperText ?? null)}
            error={!!error}
            onChange={onChange}
            value={valueOverride !== undefined ? valueOverride : (value ?? "")}
            fullWidth={fullWidth}
            label={label}
            type={type}
            size={size}
            multiline={multiline}
            minRows={minRows}
            autoFocus={autoFocus}
            disabled={disabled}
            margin={margin}
            required={required}
            id={id}
            autoComplete={autoComplete}
            variant={variant}
            slotProps={slotProps}
          />
        );
      }}
    />
  );
}

export default FormTextField;
